import styles from "./TransactionsToolbar.module.scss";
import {TRANSACTION_TYPE_TRANSFER} from "../../../codes";
import clsx from "clsx";
import {
  listConfigSelector,
  PERIOD_TYPE,
  PERIOD_TYPE_ALL,
  PERIOD_TYPE_LAST_30_DAYS,
  PERIOD_TYPE_LAST_365_DAYS,
  PERIOD_TYPE_LAST_7_DAYS,
  PERIOD_TYPE_LAST_90_DAYS,
  PERIOD_TYPE_MONTH,
  PERIOD_TYPE_PERIOD,
  PERIOD_TYPE_WEEK,
  PERIOD_TYPE_YEAR,
  rxChoosePeriod,
  rxSearch,
  rxToggleAllCategories,
  rxToggleSelectedCategory
} from "../../../slices/transactionsSlice";
import React, {useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import useFormat from "../../../util/useFormat";
import {selectAllCategories} from "../../../slices/categoriesSlice";
import {useTranslation} from "react-i18next";
import ClickAwayListener from "react-click-away-listener";
import ColoredAmount from "../../../common/ColoredAmount";
import moment from "moment";
import {CATEGORY_ID_INACTIVE, CATEGORY_ID_TRANSFER, CATEGORY_ID_UNKNOWN} from "./useTransactions";
import {DEFAULT_COLOR} from "../../../common/ColorBox";

export const TransactionsToolbar = ({transactions}) => {
  const {
    periodType: periodTypePosition,
    periodStart,
    selectedCategories,
    searchValue
  } = useSelector(listConfigSelector);

  const transactionCategories = useSelector(selectAllCategories);

  const {formatAmount} = useFormat();
  const {t} = useTranslation("transactions");

  const dispatch = useDispatch();

  const periodType = PERIOD_TYPE[periodTypePosition];

  const [periodTypesShown, setPeriodTypesShown] = useState(false);
  const [categoriesShown, setCategoriesShown] = useState(false);

  const noCategory = {
    id: CATEGORY_ID_UNKNOWN,
    name: t("category_name_unknown"),
    icon: "help_outline",
    color: DEFAULT_COLOR,
    active: true
  };

  const transferCategory = {
    id: CATEGORY_ID_TRANSFER,
    name: t("category_name_transfer"),
    icon: "swap_horiz",
    color: DEFAULT_COLOR,
    active: true
  };

  const inactiveCategories = {
    id: CATEGORY_ID_INACTIVE,
    name: t("category_name_inactive"),
    icon: "visibility_off",
    color: DEFAULT_COLOR,
    active: true
  };

  const dropdownCategories = [noCategory, transferCategory];

  if (transactionCategories.find(c => !c.active)) {
    dropdownCategories.push(inactiveCategories);
  }

  transactionCategories
    .filter(c => c.active)
    .toSorted((a, b) => (a.groupName + a.name).localeCompare(b.groupName + b.name))
    .forEach(c => dropdownCategories.push(c));

  const transactionAmounts = transactions
    .filter(t => !t.future && t.type !== TRANSACTION_TYPE_TRANSFER)
    .map(t => t.amount * t.rate);

  const inputAmount = transactionAmounts.filter(t => t >= 0).reduce((a, b) => a + b, 0);
  const outputAmount = transactionAmounts.filter(t => t < 0).reduce((a, b) => a + b, 0);
  const diffAmount = transactionAmounts.reduce((a, b) => a + b, 0);

  const updateSearchValue = e => dispatch(rxSearch({searchValue: e.target.value}));

  const toPeriod = (pt, newPeriodStart) => {
    dispatch(rxChoosePeriod({periodType: pt.position, periodStart: newPeriodStart}));
  };

  const toPreviousPeriod = () => toPeriod(periodType, periodType.getPreviousStart(periodStart));
  const toNextPeriod = () => toPeriod(periodType, periodType.getNextStart(periodStart));

  const choosePeriodType = (periodType, close, newPeriodStart = null) => e => {
    e.stopPropagation();

    if (close) {
      setPeriodTypesShown(false);
    }

    toPeriod(periodType, newPeriodStart === null ? periodStart : newPeriodStart.format("YYYY-MM-DD"));
  };

  const toggleSelectedCategory = category => {
    dispatch(rxToggleSelectedCategory({category}));
  };

  const toggleAllCategories = () => {
    const selectAll = !dropdownCategories.every(t => selectedCategories[t.id] || selectedCategories[t.id] === undefined);

    const val = {};

    dropdownCategories.forEach(t => val[t.id] = selectAll);

    dispatch(rxToggleAllCategories({selectedCategories: val}));
  };

  return <div className={styles.toolbar}>
    <div className={styles.periodChooser}>
      {periodType.getPreviousStart &&
        <div className={clsx(styles.prevNext, "hover-selectable")} onClick={toPreviousPeriod}>
          <span className="material-icons">chevron_left</span>
        </div>}
      <div className={clsx(styles.currentPeriod, "hover-selectable")} onClick={() => setPeriodTypesShown(true)}>
        {periodType.label(periodStart, t)}
        <span className="material-icons">arrow_drop_down</span>
      </div>
      {periodType.getNextStart && <div className={clsx(styles.prevNext, "hover-selectable")} onClick={toNextPeriod}>
        <span className="material-icons">chevron_right</span>
      </div>}

      {periodTypesShown && <PeriodChooser
        periodStart={periodStart}
        periodTypePosition={periodTypePosition}
        choosePeriodType={choosePeriodType}
        onClickAway={() => setPeriodTypesShown(false)}
      />}
    </div>

    <div className={styles.categoryChooser}>
      <div className={clsx(styles.currentSelection, "hover-selectable")} onClick={() => setCategoriesShown(true)}>
        <span
          className={styles.currentSelectionLabel}>{t("categories")} ({dropdownCategories.filter(t => selectedCategories[t.id] || selectedCategories[t.id] === undefined).length}/{dropdownCategories.length})</span>
        <span className="material-icons">arrow_drop_down</span>
      </div>

      {categoriesShown && <ClickAwayListener onClickAway={() => setCategoriesShown(false)}>
        <div className={styles.categories}>
          <div className={clsx(styles.category, "hover-selectable")} onClick={toggleAllCategories}>
            <div className={styles.categoryCb}>
              <span className="material-icons">
                {dropdownCategories.every(t => selectedCategories[t.id] || selectedCategories[t.id] === undefined) ? "check_box" : "check_box_outline_blank"}
              </span>
            </div>
            <span className={styles.categoryLabel}>{t("check_uncheck_all")}</span>
          </div>

          {dropdownCategories.map(cat =>
            <div className={clsx(styles.category, "hover-selectable")} key={cat.id}
                 onClick={() => toggleSelectedCategory(cat)}>
              <div className={styles.categoryCb}>
                <span className="material-icons">
                  {selectedCategories[cat.id] || selectedCategories[cat.id] === undefined ? "check_box" : "check_box_outline_blank"}
                </span>
              </div>
              <div className={clsx(styles.categoryIcon)} style={{background: cat.color.split("|")[0]}}>
                <span className="material-icons" style={{color: cat.color.split("|")[1]}}>{cat.icon}</span>
              </div>
              <span className={styles.categoryLabel}>{cat.groupName ? cat.groupName + " / " : ""}{cat.name}</span>
            </div>)}
        </div>
      </ClickAwayListener>}
    </div>

    <div className={styles.searcher}>
      <input type="text" placeholder={t("search_placeholder")} value={searchValue} onChange={updateSearchValue}/>
    </div>

    <div className={styles.balanceStat}>
      <div className={styles.statName}>{t("stat_transactions")}</div>
      <div className={styles.statValue}>
        {transactions.filter(t => t.type !== TRANSACTION_TYPE_TRANSFER).filter(t => !t.future).length}
      </div>
    </div>
    <div className={styles.balanceStat}>
      <div className={styles.statName}>{t("stat_inputs")}</div>
      <div className={styles.statValue}><ColoredAmount amount={inputAmount}>{formatAmount(inputAmount)}</ColoredAmount>
      </div>
    </div>
    <div className={styles.balanceStat}>
      <div className={styles.statName}>{t("stat_outputs")}</div>
      <div className={styles.statValue}>
        <ColoredAmount amount={outputAmount}>{formatAmount(outputAmount)}</ColoredAmount>
      </div>
    </div>
    <div className={styles.balanceStat}>
      <div className={styles.statName}>{t("stat_diff")}</div>
      <div className={styles.statValue}><ColoredAmount amount={diffAmount}>{formatAmount(diffAmount)}</ColoredAmount>
      </div>
    </div>
  </div>;
};

const PeriodChooser = ({periodStart, periodTypePosition, choosePeriodType, onClickAway}) => {
  const {t: tApp} = useTranslation("app");
  const {t} = useTranslation("transactions");

  const periodStartYear = +moment(periodStart).get("year");
  const periodStartMonth = +moment(periodStart).get("month");
  const currentYear = +moment().get("year");

  const D1 = periodStartYear - (currentYear + 2);
  const D2 = ((D1 - (((D1 % 12) + 12) % 12)) / 12) + 1;
  const D3 = currentYear + (D2 * 12);

  const [periodChooserPeriod, setPeriodChooserPeriod] = useState(() => {
    if (periodTypePosition === PERIOD_TYPE_MONTH || periodTypePosition === PERIOD_TYPE_YEAR || periodTypePosition === PERIOD_TYPE_WEEK) {
      return periodTypePosition;
    } else {
      return PERIOD_TYPE_PERIOD;
    }
  });
  const [periodChooserYearValue, setPeriodChooserYearValue] = useState(D3);
  const [periodChooserMonthValue, setPeriodChooserMonthValue] = useState(periodStartYear);
  const [periodChooserWeekValue, setPeriodChooserWeekValue] = useState(moment(periodStart).startOf("month"));

  const years = [];

  for (let i = periodChooserYearValue - 1; i > periodChooserYearValue - 11; i -= 3) {
    years.push(i);
    years.push(i + 1);
    years.push(i + 2);
  }

  const months = [
    tApp("january"),
    tApp("february"),
    tApp("march"),
    tApp("april"),
    tApp("may"),
    tApp("june"),
    tApp("july"),
    tApp("august"),
    tApp("september"),
    tApp("october"),
    tApp("november"),
    tApp("december")
  ];

  const weeks = useMemo(() => {
    const d = [];

    const currentDate = periodChooserWeekValue.clone().startOf("isoWeek");

    let week = [];

    for (let i = 0; i < 400; i++) {
      if (d.length === 6 && week.length === 7) {
        break;
      }

      if (i % 7 === 0) {
        week = [];

        d.push(week);
      }

      week.push(currentDate.clone());

      currentDate.add(1, "day");
    }

    return d;
  }, [periodChooserWeekValue]);

  return <ClickAwayListener onClickAway={onClickAway}>
    <div className={styles.periods}>
      <div className={styles.periodsTypes}>
        {[
          {type: PERIOD_TYPE_YEAR, label: "period_type_year"},
          {type: PERIOD_TYPE_MONTH, label: "period_type_month"},
          {type: PERIOD_TYPE_WEEK, label: "period_type_week"},
          {type: PERIOD_TYPE_PERIOD, label: "period_type_period"}
        ].map(p => {
          return <div
            key={p.type}
            className={clsx(styles.periodType, "hover-selectable", periodChooserPeriod === p.type ? styles.active : null)}
            onClick={() => setPeriodChooserPeriod(p.type)}>
            {t(p.label)}
          </div>;
        })}
      </div>

      <div className={styles.periodsValues}>
        {periodChooserPeriod === PERIOD_TYPE_YEAR && <>
          <div className={styles.currentYearPeriod}>
            <span className="material-icons hover-selectable"
                  onClick={() => setPeriodChooserYearValue(prev => prev - 12)}>
              chevron_left
            </span>

            <div>{years[2] + " - " + years[11]}</div>

            <span className="material-icons hover-selectable"
                  onClick={() => setPeriodChooserYearValue(prev => prev + 12)}>
              chevron_right
            </span>
          </div>

          {years.map(y => {
            const active = periodStartYear === y;

            return <div
              key={y}
              className={clsx(styles.periodYear, "hover-selectable", active ? styles.active : null)}
              onClick={choosePeriodType(PERIOD_TYPE[PERIOD_TYPE_YEAR], true, moment(periodStart).set("year", y))}>
              {y}
            </div>;
          })}
        </>}

        {periodChooserPeriod === PERIOD_TYPE_MONTH && <>
          <div className={styles.currentMonthPeriod}>
            <span className="material-icons hover-selectable"
                  onClick={() => setPeriodChooserMonthValue(prev => prev - 1)}>
              chevron_left
            </span>

            <div>{periodChooserMonthValue}</div>

            <span className="material-icons hover-selectable"
                  onClick={() => setPeriodChooserMonthValue(prev => prev + 1)}>
              chevron_right
            </span>
          </div>

          {months.map((month, i) => {
            const active = periodStartYear === periodChooserMonthValue && periodStartMonth === i;

            return <div
              key={month}
              className={clsx(styles.periodMonth, "hover-selectable", active ? styles.active : null)}
              onClick={choosePeriodType(PERIOD_TYPE[PERIOD_TYPE_MONTH], true, moment(periodStart).set("year", periodChooserMonthValue).set("month", i))}>
              {month}
            </div>;
          })}
        </>}

        {periodChooserPeriod === PERIOD_TYPE_WEEK && <>
          <div className={styles.currentWeekPeriod}>
            <span className="material-icons hover-selectable"
                  onClick={() => setPeriodChooserWeekValue(prev => prev.clone().subtract(1, "month"))}>
              chevron_left
            </span>

            <div>{periodChooserWeekValue.format("MMMM YYYY")}</div>

            <span className="material-icons hover-selectable"
                  onClick={() => setPeriodChooserWeekValue(prev => prev.clone().add(1, "month"))}>
              chevron_right
            </span>
          </div>

          <div className={styles.weekDays}>
            {weeks.map(week => {
              const active = week.some(d => periodStart === d.format("YYYY-MM-DD"));

              return <div
                key={week}
                className={clsx(styles.week, "hover-selectable", active ? styles.active : null)}
                onClick={choosePeriodType(PERIOD_TYPE[PERIOD_TYPE_WEEK], true, week[0])}
              >
                {week.map(d => {
                  const classes = [styles.periodDay];

                  let dateFormatted = d.format("YYYY/MM/DD");

                  if (d.get("month") !== periodChooserWeekValue.get("month")) {
                    classes.push(styles.otherMonth);
                  }

                  return <div key={dateFormatted} className={clsx(classes)}>{d.format("D")}</div>;
                })}
              </div>;
            })}
          </div>
        </>}

        {periodChooserPeriod === PERIOD_TYPE_PERIOD && <div className={styles.currentTypePeriod}>
          {[PERIOD_TYPE_LAST_7_DAYS, PERIOD_TYPE_LAST_30_DAYS, PERIOD_TYPE_LAST_90_DAYS, PERIOD_TYPE_LAST_365_DAYS, PERIOD_TYPE_ALL].map(period => {
            return <div key={period}
                        className={clsx(styles.period, "hover-selectable", periodTypePosition === period ? styles.active : null)}
                        onClick={choosePeriodType(PERIOD_TYPE[period], true)}>
              {PERIOD_TYPE[period].label(null, t)}
            </div>;
          })}
        </div>}
      </div>
    </div>
  </ClickAwayListener>;
};
